import { Box, Button, Divider, Tooltip, Typography } from "@mui/material";
import DOMPurify from "dompurify";
import { Form, Formik, FormikProps, FormikValues } from "formik";
import { isArray } from "lodash";
import { FC, Fragment, useEffect, useMemo, useRef } from "react";

import DatepickerFormikMUI from "web/Layout/Datepicker/datepickerFormikMUI";
import {
  BIRTH_DATE_MAX,
  BIRTH_DATE_MIN,
} from "web/Layout/SubscriptionReceivers/SubscriptionReceiverForm/ActiveForm/constants";
import __ from "web/Layout/Translations";

import CheckboxMUI from "web/Components/Common/Form/Checkbox/checkboxMUI";
import FieldMUI from "web/Components/Common/Form/Field/fieldMUI";
import PhoneWithPrefixFormMUI from "web/Components/Common/Form/PhoneWithPrefixForm/phoneWithPrefixFormMUI";
import RequirePeselFormMUI from "web/Components/Common/Form/RequirePeselForm/requirePeselFormMUI";
import SelectFormMUI from "web/Components/Common/Form/Select/selectFormMUI";

import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";
import { DEFAULT_PHONE_PREFIX } from "web/utils/page/product/subscription/forms/getPhonePrefixOptions";
import prepareActiveFormData from "web/utils/page/product/subscription/forms/prepareActiveFormData";
import { peselMask } from "web/utils/peselMask";
import { validateFields } from "web/utils/system/formValidator/validation";

import {
  ContentTypeActiveFormData,
  HeaderTypeActiveFormData,
  IActiveFormValues,
  ISubscriptionFormData,
  ISubscriptionReceiverFormField,
  ISubscriptionReceiverFormValues,
  PhoneWithPrefixField,
  SubscriptionActiveFormData,
  SubscriptionActiveFormDataType,
  SubscriptionActiveFormField,
  SubscriptionActiveFormFieldType,
} from "web/types/Subscription";

import {
  ClearDisabledDependentFieldsEffect,
  getDisabledDependentFieldIds,
} from "./activeFormDependencies";
import {
  PeselBirthDateAutoFillEffect,
  getPeselBirthDatePairs,
} from "./peselBirthDateAutoFill";

interface IActiveFormProps {
  configuration: SubscriptionActiveFormData[];
  fields: ISubscriptionReceiverFormField[];
  formData: ISubscriptionFormData;
  lang?: {
    key: string;
    code: string;
  };
  nameFieldsDisabled: boolean;
  submitHandler: (values: ISubscriptionReceiverFormValues) => void;
  closeHandler: () => void;
  isMobile: boolean;
  onDirtyChange: (isDirty: boolean) => void;
  excludeNameFieldsFromDependencies?: boolean;
}

const ActiveFormMUI: FC<IActiveFormProps> = ({
  configuration,
  fields,
  formData,
  lang = { key: "pl_PL", code: "pl" },
  nameFieldsDisabled,
  submitHandler,
  closeHandler,
  isMobile,
  onDirtyChange,
  excludeNameFieldsFromDependencies = false,
}) => {
  if (!isArray(fields) || !configuration) return null;

  const ref = useRef<FormikProps<FormikValues>>(null);

  const buttonsText =
    lang.key === "pl_PL" ? __("Zapisz i zamknij") : "Save and close";
  const selectPlaceholder =
    lang.key === "pl_PL" ? __("Wybierz opcję") : "Select option";

  const getHeader = (header: HeaderTypeActiveFormData) => {
    return (
      <Typography variant="subtitle1" key={header.id} mb={1}>
        {header.value}
      </Typography>
    );
  };

  const getContent = (content: ContentTypeActiveFormData) => {
    if (!content.content) {
      return null;
    }

    const contentElement = (
      <Box
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(content.content),
        }}
        sx={{
          m: 0,
          "& > p": {
            m: 0,
          },
        }}
      />
    );
    return (
      <Box key={content.id} sx={{ mb: 2 }}>
        <Typography variant="subtitle1" mb={1}>
          {content.name}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "flex-start" }}>
          {content?.useCheckbox ? (
            <CheckboxMUI name={content.id} dataT1={content.name}>
              {contentElement}
            </CheckboxMUI>
          ) : (
            contentElement
          )}
        </Box>
      </Box>
    );
  };

  const getField = (
    field: SubscriptionActiveFormField,
    dependencyDisabled: boolean
  ) => {
    let input;
    let initialValuesPlaceholder;

    switch (field.type) {
      case SubscriptionActiveFormFieldType.REQUIRE_PESEL:
        input = (
          <RequirePeselFormMUI
            field={field}
            tooltipMessage={field.tooltip}
            lang={lang}
            isMobile={isMobile}
            disabled={dependencyDisabled}
          />
        );
        break;
      case SubscriptionActiveFormFieldType.PHONE_WITH_PREFIX:
        input = (
          <PhoneWithPrefixFormMUI
            field={field as PhoneWithPrefixField}
            lang={lang}
            isMobile={isMobile}
            tooltipMessage={field.tooltip}
            disabled={dependencyDisabled}
          />
        );
        break;
      case SubscriptionActiveFormFieldType.SELECT:
        initialValuesPlaceholder =
          field?.options?.find(
            (el) => el.value === (initialValues as IActiveFormValues)[field.id]
          )?.label || (initialValues as IActiveFormValues)[field.id];
        input = (
          <SelectFormMUI
            label={field.label}
            name={field.id}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            placeholder={initialValuesPlaceholder || selectPlaceholder}
            options={field.options.map((option) => ({
              value: option.value,
              label: option.label,
            }))}
            dataT1={field.label}
            sx={{ mt: 2, mb: 0 }}
            disabled={dependencyDisabled}
          />
        );
        break;
      case SubscriptionActiveFormFieldType.CHECKBOX:
        input = (
          <CheckboxMUI
            name={field.id}
            tooltipMessage={field.tooltip}
            dataT1={field.label}
            disabled={dependencyDisabled}
          >
            {field.label}
          </CheckboxMUI>
        );
        break;
      case SubscriptionActiveFormFieldType.BIRTHDATE:
      case SubscriptionActiveFormFieldType.DATE: {
        const isBirthDate =
          field.type === SubscriptionActiveFormFieldType.BIRTHDATE;

        const minDateValue = isBirthDate ? BIRTH_DATE_MIN : null;
        const maxDateValue = isBirthDate ? BIRTH_DATE_MAX : null;

        input = (
          <DatepickerFormikMUI
            name={field.id}
            label={field.label}
            placeholder="DD/MM/YYYY"
            minDate={minDateValue}
            maxDate={maxDateValue}
            dataT1={field.label}
            isMobile={isMobile}
            sx={{ mt: 2, mb: 0, maxWidth: "512px" }}
            disabled={dependencyDisabled}
          />
        );
        break;
      }
      case SubscriptionActiveFormFieldType.POSTCODE:
        input = (
          <FieldMUI
            label={field.label}
            name={field.id}
            dataT1={field.label}
            sx={{ mb: 0, maxWidth: "512px" }}
            placeholder="_ _ - _ _ _"
            isDataT1NotChanged
            disabled={dependencyDisabled}
          />
        );
        break;
      case SubscriptionActiveFormFieldType.FIRSTNAME:
      case SubscriptionActiveFormFieldType.LASTNAME: {
        input = (
          <FieldMUI
            label={field.label}
            name={field.id}
            disabled={nameFieldsDisabled || dependencyDisabled}
            dataT1={field.label}
            sx={{ mb: 0, maxWidth: "512px" }}
            isDataT1NotChanged
          />
        );
        break;
      }
      case SubscriptionActiveFormFieldType.PESEL: {
        input = (
          <FieldMUI
            label={field.label}
            name={field.id}
            mask={peselMask}
            dataT1={field.label}
            placeholder="- - - - - - - - - - -"
            sx={{ mb: 0, maxWidth: "512px" }}
            isDataT1NotChanged
            disabled={dependencyDisabled}
          />
        );
        break;
      }
      case SubscriptionActiveFormFieldType.NUMBER: {
        input = (
          <FieldMUI
            label={field.label}
            name={field.id}
            dataT1={field.label}
            sx={{ mb: 0, maxWidth: "512px" }}
            isDataT1NotChanged
            enableNumericHandler
            disabled={dependencyDisabled}
          />
        );
        break;
      }
      default: {
        input = (
          <FieldMUI
            label={field.label}
            name={field.id}
            type={field.type}
            dataT1={field.label}
            sx={{ mb: 0, maxWidth: "512px" }}
            isDataT1NotChanged
            disabled={dependencyDisabled}
          />
        );
      }
    }

    const doesFieldHaveTooltip = field.tooltip?.trim();
    return (
      <Box
        component="fieldset"
        disabled={dependencyDisabled}
        key={field.id}
        sx={{
          border: 0,
          m: 0,
          minWidth: 0,
          opacity: dependencyDisabled ? 0.5 : 1,
          p: 0,
        }}
      >
        {doesFieldHaveTooltip ? (
          <Tooltip title={field.tooltip} placement="right">
            {input}
          </Tooltip>
        ) : (
          input
        )}
      </Box>
    );
  };

  const [initialValues, fieldsToValidate] = useMemo(
    () => prepareActiveFormData(fields, formData, lang),
    [fields]
  );

  const peselBirthDatePairs = useMemo(
    () => getPeselBirthDatePairs(fields),
    [fields]
  );

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const validate = validateFields(fieldsToValidate);

  // For PHONE_WITH_PREFIX fields: include prefix in payload only when phone number
  // is filled (and thus passed validation). If phone is empty, clear the prefix.
  const handleSubmit = (values: ISubscriptionReceiverFormValues) => {
    const transformedValues = { ...values } as Record<string, unknown>;

    fields.forEach((field) => {
      if (field.type === SubscriptionActiveFormFieldType.PHONE_WITH_PREFIX) {
        const phoneFieldValue = transformedValues[field.id] as {
          prefix: string;
          phoneNumber: string;
        };
        if (!phoneFieldValue?.phoneNumber) {
          transformedValues[field.id] = { ...phoneFieldValue, prefix: "" };
        } else if (!phoneFieldValue?.prefix) {
          transformedValues[field.id] = {
            ...phoneFieldValue,
            prefix: DEFAULT_PHONE_PREFIX,
          };
        }
      }
    });

    submitHandler(transformedValues as ISubscriptionReceiverFormValues);
  };

  const getFormBody = (disabledFieldIds: Set<string>) => {
    if (!isArrayHasItems(configuration)) return null;

    return configuration.map((section) => {
      switch (section.type) {
        case SubscriptionActiveFormDataType.HEADER:
          return getHeader(section);
        case SubscriptionActiveFormDataType.CONTENT:
          return getContent(section);
        case SubscriptionActiveFormDataType.FORM:
          return (
            <Fragment key={section.id}>
              {section.fields.map((field) =>
                getField(field, disabledFieldIds.has(field.id))
              )}
              {/* <div className={styles.separator} /> */}
              <Divider sx={{ my: 3 }} />
            </Fragment>
          );
        default:
          return null;
      }
    });
  };

  const dependencyExcludedTypes = excludeNameFieldsFromDependencies
    ? [
        SubscriptionActiveFormFieldType.FIRSTNAME,
        SubscriptionActiveFormFieldType.LASTNAME,
      ]
    : [];

  return (
    <Formik
      enableReinitialize
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      initialValues={initialValues}
      onSubmit={handleSubmit}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      validate={validate}
      innerRef={ref}
    >
      {({ dirty, values }) => {
        useEffect(() => {
          onDirtyChange(dirty);
        }, [dirty]);

        const disabledFieldIds = getDisabledDependentFieldIds(
          fields,
          values,
          dependencyExcludedTypes
        );

        return (
          <Form noValidate>
            <ClearDisabledDependentFieldsEffect
              fields={fields}
              excludedTypes={dependencyExcludedTypes}
            />
            {peselBirthDatePairs.length > 0 && (
              <PeselBirthDateAutoFillEffect pairs={peselBirthDatePairs} />
            )}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                ...(isMobile && {
                  px: 2,
                  pt: 3,
                  backgroundColor: "#fff",
                  borderTopRightRadius: "16px",
                  borderTopLeftRadius: "16px",
                  boxShadow:
                    "0px 1px 1px #F2F2F2, 0px 4px 24px rgba(166, 166, 166, 0.2)",
                }),
              }}
            >
              {getFormBody(disabledFieldIds)}
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                ...(isMobile && {
                  background: "#fff",
                  pb: 3,
                  px: 2,
                  pt: 1,
                  flexDirection: "column",
                }),
              }}
            >
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={closeHandler}
                sx={{
                  mr: isMobile ? 0 : 1,
                  ...(isMobile && { order: 1 }),
                }}
              >
                {__("Anuluj")}
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                sx={{ ...(isMobile && { mb: 2 }) }}
              >
                {buttonsText}
              </Button>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ActiveFormMUI;
